import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Download, LogOut, Shield, UserCheck, UserX, Users, Mail, Crown, Activity } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PW_KEY = "hitpath_admin_pw";
const isPaid = (u) => u.plan === "pro" || u.plan === "lifetime";

// Helper that always attaches the admin password header.
const adminFetch = async (method, path, pw, body) => {
  const res = await axios({
    method,
    url: `${API}${path}`,
    headers: { "X-Admin-Password": pw },
    data: body,
    responseType: path.endsWith(".csv") ? "blob" : "json",
    validateStatus: () => true,
  });
  return res;
};

const StatCard = ({ icon: Icon, label, value, tone = "amber", testid }) => {
  const tones = {
    amber: "text-amber-300 bg-amber-500/10 border-amber-500/40",
    emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/40",
    cyan: "text-cyan-300 bg-cyan-500/10 border-cyan-500/40",
    slate: "text-slate-300 bg-slate-800/60 border-slate-700",
  };
  return (
    <Card className="bg-[#131c2e]/90 border-slate-800" data-testid={testid}>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`w-11 h-11 rounded-lg border flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-heading">{label}</div>
          <div className="text-2xl font-heading font-bold text-slate-50 font-mono-digit">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const LoginGate = ({ onAuth }) => {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await adminFetch("post", "/admin/verify", pw);
    setLoading(false);
    if (res.status === 200) {
      sessionStorage.setItem(PW_KEY, pw);
      onAuth(pw);
    } else {
      toast.error("Wrong password");
    }
  };
  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-100 flex items-center justify-center p-6">
      <Card className="w-full max-w-md bg-[#131c2e]/90 border-slate-800 shadow-2xl shadow-black/60">
        <CardContent className="p-8 space-y-5">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-amber-300" />
            <h1 className="font-heading text-2xl font-bold text-slate-50">Admin</h1>
          </div>
          <p className="text-sm text-slate-400">Enter the admin password to continue.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="pw" className="text-slate-300">Password</Label>
              <Input
                id="pw"
                type="password"
                autoFocus
                required
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="h-11 bg-slate-900/70 border-slate-700 text-slate-100 mt-1"
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-heading font-bold"
              data-testid="admin-login-btn"
            >
              {loading ? "Checking…" : "Enter"}
            </Button>
          </form>
          <div className="pt-2 border-t border-slate-800">
            <Link to="/" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300 transition">
              <ArrowLeft className="w-3 h-3" />
              Back to Hit Path
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboard = ({ pw, onLogout }) => {
  const [stats, setStats] = useState({ leads: 0, users: 0, pro_users: 0, workouts: 0 });
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("pro_monthly");
  const [working, setWorking] = useState(false);

  const refresh = async () => {
    const [s, u] = await Promise.all([
      adminFetch("get", "/admin/stats", pw),
      adminFetch("get", "/admin/users", pw),
    ]);
    if (s.status === 200) setStats(s.data);
    if (u.status === 200) setUsers(u.data.users || []);
    if (s.status === 401 || u.status === 401) {
      toast.error("Session expired — sign in again");
      onLogout();
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadLeads = async () => {
    const res = await adminFetch("get", "/admin/leads.csv", pw);
    if (res.status !== 200) {
      toast.error("Could not download leads");
      return;
    }
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hitpath-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Leads CSV downloaded");
  };

  const upgrade = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setWorking(true);
    const res = await adminFetch("post", "/admin/upgrade-user", pw, { email, plan });
    setWorking(false);
    if (res.status === 200) {
      toast.success(`${email} upgraded to ${plan.replace("_", " ")}`);
      setEmail("");
      refresh();
    } else if (res.status === 404) {
      toast.error("No user with that email — they must register first.");
    } else {
      toast.error("Upgrade failed");
    }
  };

  const downgrade = async (targetEmail) => {
    if (!window.confirm(`Remove Pro from ${targetEmail}?`)) return;
    const res = await adminFetch("post", "/admin/downgrade-user", pw, { email: targetEmail });
    if (res.status === 200) {
      toast.success(`${targetEmail} downgraded`);
      refresh();
    } else {
      toast.error("Downgrade failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-100">
      <div className="sticky top-0 z-10 border-b border-slate-800 bg-[#0b0f17]/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-300" />
            <span className="font-heading font-bold text-slate-100">Hit Path · Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm text-slate-400 hover:text-amber-300 transition">
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              App
            </Link>
            <Button
              onClick={onLogout}
              variant="outline"
              size="sm"
              className="h-9 border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Log out
            </Button>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 space-y-8">
        {/* STATS */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
          <StatCard icon={Mail} label="Leads" value={stats.leads} tone="cyan" testid="stat-leads" />
          <StatCard icon={Users} label="Users" value={stats.users} tone="slate" testid="stat-users" />
          <StatCard icon={Crown} label="Pro Users" value={stats.pro_users} tone="amber" testid="stat-pro-users" />
          <StatCard icon={Activity} label="Workouts" value={stats.workouts} tone="emerald" testid="stat-workouts" />
        </section>

        {/* ACTIONS GRID */}
        <section className="grid lg:grid-cols-2 gap-6">
          {/* LEADS EXPORT */}
          <Card className="bg-[#131c2e]/90 border-slate-800 self-start">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-cyan-300" />
                <h2 className="font-heading text-xl font-bold text-slate-50">Leads</h2>
              </div>
              <p className="text-sm text-slate-400">
                Everyone who grabbed the pamphlet PDF. Download as CSV to import into Mailchimp,
                Google Sheets, or Resend for your next email blast.
              </p>
              <Button
                onClick={downloadLeads}
                className="h-11 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-heading font-bold"
                data-testid="download-leads-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download leads CSV ({stats.leads})
              </Button>
            </CardContent>
          </Card>

          {/* UPGRADE CUSTOMER */}
          <Card className="bg-[#131c2e]/90 border-amber-500/30">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-300" />
                <h2 className="font-heading text-xl font-bold text-slate-50">Activate Pro</h2>
              </div>
              <p className="text-sm text-slate-400">
                After a Square receipt arrives, paste the customer email and pick the plan they bought.
                The customer must have already signed up / registered for Hit Path.
              </p>
              <form onSubmit={upgrade} className="space-y-3">
                <div>
                  <Label className="text-slate-300">Customer email</Label>
                  <Input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="customer@example.com"
                    className="h-11 bg-slate-900/70 border-slate-700 text-slate-100 mt-1"
                    data-testid="upgrade-email-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Plan</Label>
                  <Select value={plan} onValueChange={setPlan}>
                    <SelectTrigger className="h-11 bg-slate-900/70 border-slate-700 text-slate-100 mt-1" data-testid="upgrade-plan-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pro_monthly">Pro Monthly ($6.99/mo)</SelectItem>
                      <SelectItem value="pro_yearly">Pro Yearly ($49/yr)</SelectItem>
                      <SelectItem value="lifetime">Lifetime ($79)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="submit"
                  disabled={working}
                  className="h-11 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-heading font-bold"
                  data-testid="upgrade-submit-btn"
                >
                  <UserCheck className="w-4 h-4 mr-2" />
                  {working ? "Activating…" : "Activate Pro"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </section>

        {/* USERS LIST */}
        <section>
          <Card className="bg-[#131c2e]/90 border-slate-800">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-slate-300" />
                  <h2 className="font-heading text-xl font-bold text-slate-50">Users</h2>
                </div>
                <Button
                  onClick={refresh}
                  variant="outline"
                  size="sm"
                  className="h-9 border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="admin-refresh-btn"
                >
                  Refresh
                </Button>
              </div>
              <div className="overflow-x-auto" data-testid="users-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-widest text-slate-500 font-heading border-b border-slate-800">
                      <th className="py-3 pr-4">Email</th>
                      <th className="py-3 pr-4">Name</th>
                      <th className="py-3 pr-4">Pro</th>
                      <th className="py-3 pr-4">Plan</th>
                      <th className="py-3 pr-4">Signed up</th>
                      <th className="py-3 pr-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-slate-500">
                          No users yet.
                        </td>
                      </tr>
                    )}
                    {users.map((u) => (
                      <tr key={u.user_id || u.email} className="border-b border-slate-800/60 hover:bg-slate-900/40 transition">
                        <td className="py-3 pr-4 text-slate-200">{u.email}</td>
                        <td className="py-3 pr-4 text-slate-400">{u.name || "—"}</td>
                        <td className="py-3 pr-4">
                          {isPaid(u) ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/40 text-xs font-heading font-bold">
                              <Crown className="w-3 h-3" /> Pro
                            </span>
                          ) : (
                            <span className="text-slate-500 text-xs">Free</span>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-slate-400 text-xs">{u.plan_lookup || u.plan || "—"}</td>
                        <td className="py-3 pr-4 text-slate-500 text-xs">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="py-3 pr-4 text-right">
                          {isPaid(u) && (
                            <Button
                              onClick={() => downgrade(u.email)}
                              variant="outline"
                              size="sm"
                              className="h-8 border-slate-700 text-slate-300 hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-300"
                              data-testid={`downgrade-${u.email}`}
                            >
                              <UserX className="w-3 h-3 mr-1" />
                              Remove Pro
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
};

export default function Admin() {
  const [pw, setPw] = useState(() => sessionStorage.getItem(PW_KEY) || "");

  if (!pw) {
    return <LoginGate onAuth={setPw} />;
  }
  return (
    <AdminDashboard
      pw={pw}
      onLogout={() => {
        sessionStorage.removeItem(PW_KEY);
        setPw("");
      }}
    />
  );
}
